import {
  TFieldSchema,
  TPaymentTerminalProvider,
  TRestaurantPaymentConfig,
} from '../schemas/payment-provider-schema';

/**
 * Admin reads return stored secrets as this literal mask — only the kiosk read
 * endpoint returns plaintext. A field holding the mask has a stored value whose
 * contents we cannot inspect.
 */
export const MASK = '••••••••';

export type TAppEnv = 'staging' | 'production';

export type TEnvKeyWarning = {
  fieldId: string;
  prefix: string;
};

export type TConfigHealth = {
  missingRequired: string[];
  envWarnings: TEnvKeyWarning[];
  isReady: boolean;
};

/**
 * The kiosk read endpoint silently DROPS any provider whose stored key shape
 * mismatches the environment. A dropped provider is absent from the kiosk's
 * response, which kiosks treat as "not configured" — card payment is refused
 * and there is no fallback path.
 */
const DROPPED_PREFIXES: Record<TAppEnv, string[]> = {
  production: ['sk_sandbox_', 'sk_test_', 'sup_sk_test_'],
  staging: ['sk_prod_', 'sk_live_', 'sup_sk_live_'],
};

export const resolveEnv = (): TAppEnv => {
  const explicit = process.env.REACT_APP_ENV;
  if (explicit === 'staging' || explicit === 'production') return explicit;
  // Fall back to the admin host so a deploy predating REACT_APP_ENV still works.
  return (process.env.REACT_APP_ADMIN_API_URL || '').includes('api.prod.')
    ? 'production'
    : 'staging';
};

/**
 * The client-scope fields a restaurant config is expected to fill.
 *
 * Prefers the live catalog schema, which carries `scope`. The row's own schema
 * snapshot has no `scope` key — it is already client-only — so it is used
 * unfiltered as a fallback when the catalog hasn't loaded.
 */
export const getClientFieldSchema = (
  provider?: TPaymentTerminalProvider | null,
  config?: TRestaurantPaymentConfig | null,
): TFieldSchema[] => {
  const catalogFields = provider?.schema?.fields || [];
  if (catalogFields.length > 0) {
    return catalogFields.filter((f) => f.scope === 'client');
  }
  return config?.schema?.fields || [];
};

const hasValue = (value: unknown): boolean =>
  typeof value === 'string' && value.trim() !== '';

/**
 * Required client fields with no stored value. The backend does NOT enforce
 * required fields, so a partial row saves happily and then reads as
 * "not configured" on the kiosk.
 */
export const getMissingRequiredClientFields = (
  config: TRestaurantPaymentConfig,
  provider?: TPaymentTerminalProvider | null,
): string[] =>
  getClientFieldSchema(provider, config)
    .filter((f) => f.required)
    .filter((f) => !hasValue(config?.clientFields?.[f.id]))
    .map((f) => f.id);

/**
 * Values whose prefix will get the provider dropped in the current environment.
 *
 * Note: stored secrets read back as MASK, so this cannot inspect them. For
 * saved rows it therefore only catches non-secret fields; the reliable check is
 * at type-time in the config dialog, where the plaintext is available.
 */
export const getEnvKeyShapeWarnings = (
  clientFields: Record<string, string> | undefined,
  env: TAppEnv = resolveEnv(),
): TEnvKeyWarning[] => {
  if (!clientFields) return [];
  const prefixes = DROPPED_PREFIXES[env];

  return Object.entries(clientFields).reduce<TEnvKeyWarning[]>((acc, [fieldId, value]) => {
    if (typeof value !== 'string' || value === MASK) return acc;
    const prefix = prefixes.find((p) => value.startsWith(p));
    if (prefix) acc.push({ fieldId, prefix });
    return acc;
  }, []);
};

export const getConfigHealth = (
  config: TRestaurantPaymentConfig,
  provider?: TPaymentTerminalProvider | null,
  env: TAppEnv = resolveEnv(),
): TConfigHealth => {
  const missingRequired = getMissingRequiredClientFields(config, provider);
  const envWarnings = getEnvKeyShapeWarnings(config?.clientFields, env);

  return {
    missingRequired,
    envWarnings,
    isReady: missingRequired.length === 0 && envWarnings.length === 0,
  };
};
